import { useState } from 'react'
import { KpiCard } from '../components/KpiCard'
import { CostChart } from '../components/CostChart'
import { ServiceCard } from '../components/ServiceCard'
import { useAdminData } from '../hooks/useAdminData'

interface ModelBreakdown {
  model: string
  calls: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  cache_creation_tokens: number
  cost_usd: number
}

interface SourceBreakdown {
  source: string
  calls: number
  cost_usd: number
  input_tokens?: number
  output_tokens?: number
}

interface UserBreakdown {
  user_id: string | null
  email: string
  calls: number
  cost_usd: number
}

interface CostsData {
  period_days: number
  total_cost_usd: number
  daily_avg_usd: number
  monthly_projection_usd: number
  anthropic: {
    cost_usd: number
    input_tokens: number
    output_tokens: number
    cached_tokens: number
    cache_creation_tokens: number
    by_model: ModelBreakdown[]
    by_source: SourceBreakdown[]
    by_user?: UserBreakdown[]
  }
  voyage: Record<string, any>
  daily_costs: Array<{
    date: string
    total: number
    anthropic: number
    voyage: number
    active_users: number
    cost_per_user: number
  }>
}

// Compact the Anthropic model id for display. We don't want
// "claude-sonnet-4-20250514" taking 30 chars in a breakdown row when
// "sonnet-4" is what the operator actually cares about when comparing.
function shortModel(model: string): string {
  const m = model.match(/claude-([a-z]+)-(\d+(?:-\d+)?)/)
  if (!m) return model
  return `${m[1]}-${m[2].replace('-', '.')}`
}

const PERIODS = [
  { label: 'Hoje', days: 0 },
  { label: '7 dias', days: 7 },
  { label: '30 dias', days: 30 },
  { label: '90 dias', days: 90 },
]

export function CostosPage() {
  const [days, setDays] = useState(0)
  const { data, loading } = useAdminData<CostsData>(`/costs?days=${days}`, [days])

  if (loading || !data) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--ink-muted)', fontFamily: 'var(--sans)' }}>
        {loading ? 'A carregar...' : 'Sem dados'}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header with period selector */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1
          style={{
            fontFamily: 'var(--serif)',
            fontSize: '28px',
            fontWeight: 600,
            color: 'var(--ink)',
            margin: 0,
          }}
        >
          Custos
        </h1>
        <div style={{ display: 'flex', gap: '4px' }}>
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                background: days === p.days ? 'var(--ink)' : 'transparent',
                color: days === p.days ? '#fff' : 'var(--ink-light)',
                fontFamily: 'var(--sans)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <KpiCard label="Total no Periodo" value={`$${data.total_cost_usd.toFixed(2)}`} />
        <KpiCard label="Media Diaria" value={`$${data.daily_avg_usd.toFixed(2)}`} />
        <KpiCard
          label="Projecao Mensal"
          value={`$${data.monthly_projection_usd.toFixed(0)}`}
          accent
        />
      </div>

      {/* Chart */}
      <CostChart data={data.daily_costs} />

      {/* Service breakdown */}
      <div>
        <h2
          style={{
            fontFamily: 'var(--serif)',
            fontSize: '20px',
            fontWeight: 600,
            color: 'var(--ink)',
            margin: '0 0 16px',
          }}
        >
          Breakdown por Servico
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <ServiceCard
            name="Anthropic"
            color="#7c3aed"
            costUsd={data.anthropic.cost_usd || 0}
            details={{
              'Input tokens': data.anthropic.input_tokens?.toLocaleString() || '0',
              'Output tokens': data.anthropic.output_tokens?.toLocaleString() || '0',
              'Cache read': data.anthropic.cached_tokens?.toLocaleString() || '0',
              'Cache write': data.anthropic.cache_creation_tokens?.toLocaleString() || '0',
            }}
            breakdowns={[
              {
                title: 'Por modelo',
                rows: (data.anthropic.by_model || []).map((m: any) => ({
                  // Short-label the model — the full string (claude-sonnet-4-20250514)
                  // is verbose; "sonnet-4" / "haiku-4" is what matters operationally.
                  label: shortModel(m.model),
                  cost_usd: m.cost_usd,
                  calls: m.calls,
                })),
              },
              {
                title: 'Por source (agent/chat)',
                rows: (data.anthropic.by_source || []).map((s: any) => ({
                  label: s.source,
                  cost_usd: s.cost_usd,
                  calls: s.calls,
                })),
              },
              {
                title: 'Por user',
                rows: (data.anthropic.by_user || []).map((u: any) => ({
                  label: u.email,
                  cost_usd: u.cost_usd,
                  calls: u.calls,
                })),
              },
            ]}
          />
          <ServiceCard
            name="Voyage AI (embeddings)"
            color="#0891b2"
            costUsd={data.voyage.cost_usd || 0}
            details={{
              'Chunks embebidos': data.voyage.chunks_embedded?.toLocaleString() || '0',
              'Tokens estimados': data.voyage.tokens_estimated?.toLocaleString() || '0',
              'Modelo': 'voyage-3',
              'Preço': '$0.06/1M tokens',
            }}
          />
        </div>
        {/* Nota: infra cost (Supabase/Vercel/Railway) removida em s45 —
            os collectors dependiam de tokens nunca configurados e caíam
            em fallback hardcoded a pricing de tier. Ruído sem signal.
            Reintroduz quando os tokens estiverem configurados e o cost
            real vier das APIs de billing. */}
      </div>
    </div>
  )
}
